"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { log } from "@/lib/logger";
import { clearStepDrafts } from "@/lib/hooks/use-step-draft";

export type SelectionKind = "look" | "described" | "spotted";

export type SelectionItem = {
  id: string;
  kind: SelectionKind;
  title: string;
  sub?: string;
  /** Pour kind=look : liste de tones pour mini-thumbs. */
  tones?: string[];
  /** Look chargé par la cliente (photothèque/caméra) : data URL ou URL de la photo. */
  imageUrl?: string;
  /** Pour kind=look prédéfini : slug du look → permet de ré-éditer la sélection. */
  lookSlug?: string;
  /** Couleur choisie (hex) pour kind=described/spotted → pastille à la place du libellé « Couleur N ». */
  colorHex?: string;
  createdAt: number;
};

export type Budget =
  | { kind: "bracket"; label: string; sub: string }
  | { kind: "none" }
  | null;

export type Appointment = {
  id: string;
  locationId: string;
  locationName: string;
  locationAddress: string;
  personalShopperFirstName: string;
  personalShopperTone: string;
  dateLabel: string;
  startTimeLabel: string;
  endTimeLabel: string;
  durationLabel: string;
  selectionSummary: string;
  /** Budget affiché sur sa propre ligne au récap de confirmation (null si non renseigné). */
  budgetLabel: string | null;
  createdAt: number;
};

type SelectionState = {
  items: SelectionItem[];
  budget: Budget;
  bookingDraft: {
    locationId: string | null;
    /** PS choisi (UUID Supabase). null si pas encore choisi OU « pas de préférence ». */
    personalShopperId: string | null;
    /** Prénom du PS choisi (affichage). null si « pas de préférence ». */
    personalShopperFirstName: string | null;
    /** true si la cliente a explicitement choisi « pas de préférence » (auto-assign serveur). */
    personalShopperNoPreference: boolean;
    dateISO: string | null;
    slotLabel: string | null;
  };
  appointments: Appointment[];
};

type SelectionContextValue = SelectionState & {
  /** true une fois le panier relu depuis localStorage (évite les guards prématurés). */
  hydrated: boolean;
  addItem: (item: Omit<SelectionItem, "id" | "createdAt">) => void;
  removeItem: (id: string) => void;
  /** Remplace TOUS les items « look » par la nouvelle liste (édition de la sélection). */
  replaceLookItems: (looks: Omit<SelectionItem, "id" | "createdAt">[]) => void;
  clearSelection: () => void;
  setBudget: (b: Budget) => void;
  setBookingDraft: (patch: Partial<SelectionState["bookingDraft"]>) => void;
  resetBookingDraft: () => void;
  confirmAppointment: (a: Omit<Appointment, "id" | "createdAt">) => Appointment;
};

const SelectionContext = createContext<SelectionContextValue | null>(null);

const EMPTY: SelectionState = {
  items: [],
  budget: null,
  bookingDraft: {
    locationId: null,
    personalShopperId: null,
    personalShopperFirstName: null,
    personalShopperNoPreference: false,
    dateISO: null,
    slotLabel: null,
  },
  appointments: [],
};

function storageKey(partnerSlug: string, scopeId: string) {
  return `fyt:selection:${partnerSlug}:${scopeId}`;
}

function genId() {
  return `s_${Math.random().toString(36).slice(2, 10)}`;
}

export function SelectionProvider({
  partnerSlug,
  scopeId = "anon",
  serverItems = [],
  children,
}: {
  partnerSlug: string;
  /** Identifiant de la cliente → panier isolé par compte (pas partagé sur l'appareil). */
  scopeId?: string;
  /** Panier reconstruit depuis la base = SOURCE DE VÉRITÉ des items (évite la
   *  divergence localStorage qui faisait « disparaître » des envies). */
  serverItems?: SelectionItem[];
  children: ReactNode;
}) {
  const [state, setState] = useState<SelectionState>(EMPTY);
  const [hydrated, setHydrated] = useState(false);
  // Clé stable du panier serveur → ne ré-applique que quand son contenu change.
  const serverKey = serverItems.map((i) => i.id).join("|");

  // Hydratation : budget / RDV / brouillon de réservation depuis localStorage,
  // mais les ITEMS viennent du serveur (source de vérité).
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(storageKey(partnerSlug, scopeId));
      const parsed = raw ? (JSON.parse(raw) as Partial<SelectionState>) : {};
      setState({ ...EMPTY, ...parsed, items: serverItems });
    } catch (err) {
      log.error("SelectionProvider · hydratation impossible", err);
      setState({ ...EMPTY, items: serverItems });
    } finally {
      setHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerSlug, scopeId]);

  // Le panier serveur change (ajout/suppression confirmés) → on réaligne les items.
  useEffect(() => {
    if (!hydrated) return;
    setState((s) => ({ ...s, items: serverItems }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey, hydrated]);

  // Persistance : on NE persiste PAS les items (serveur = vérité ; évite aussi le
  // quota localStorage avec les photos/inspirations en data URL).
  useEffect(() => {
    if (!hydrated) return;
    try {
      // On ne persiste QUE budget / réservation / RDV (pas les items = serveur).
      const persisted = {
        budget: state.budget,
        bookingDraft: state.bookingDraft,
        appointments: state.appointments,
      };
      window.localStorage.setItem(storageKey(partnerSlug, scopeId), JSON.stringify(persisted));
    } catch (err) {
      log.error("SelectionProvider · persistance impossible", err);
    }
  }, [state, partnerSlug, scopeId, hydrated]);

  const addItem: SelectionContextValue["addItem"] = useCallback((item) => {
    // Garde-fou anti-doublon : si une pièce strictement identique existe déjà
    // (même type / libellé / couleur / image), on ne l'ajoute pas deux fois —
    // évite le doublon au retour arrière + re-sélection (retour Djam).
    const sig = (it: Pick<SelectionItem, "kind" | "title" | "sub" | "colorHex" | "imageUrl">) =>
      `${it.kind}|${it.title}|${it.sub ?? ""}|${it.colorHex ?? ""}|${it.imageUrl ?? ""}`;
    setState((s) => {
      if (s.items.some((x) => sig(x) === sig(item))) {
        log.state("selection · addItem ignoré (doublon)", item);
        return s;
      }
      return {
        ...s,
        items: [...s.items, { ...item, id: genId(), createdAt: Date.now() }],
      };
    });
    log.state("selection · addItem", item);
  }, []);

  const removeItem: SelectionContextValue["removeItem"] = useCallback((id) => {
    setState((s) => ({ ...s, items: s.items.filter((x) => x.id !== id) }));
    log.state("selection · removeItem", { id });
  }, []);

  const replaceLookItems: SelectionContextValue["replaceLookItems"] = useCallback((looks) => {
    setState((s) => ({
      ...s,
      items: [
        ...s.items.filter((x) => x.kind !== "look"),
        ...looks.map((l) => ({ ...l, id: genId(), createdAt: Date.now() })),
      ],
    }));
    log.state("selection · replaceLookItems", { count: looks.length });
  }, []);

  const clearSelection = useCallback(() => {
    setState(EMPTY);
    // Sélection vidée → on purge aussi les brouillons d'étapes (looks/pièce/couleur)
    // pour repartir vierge à la prochaine envie.
    clearStepDrafts("look-");
    clearStepDrafts("piece-");
    clearStepDrafts("color-");
    log.state("selection · clear");
  }, []);

  const setBudget: SelectionContextValue["setBudget"] = useCallback((b) => {
    setState((s) => ({ ...s, budget: b }));
    log.state("selection · setBudget", b);
  }, []);

  const setBookingDraft: SelectionContextValue["setBookingDraft"] = useCallback((patch) => {
    setState((s) => ({ ...s, bookingDraft: { ...s.bookingDraft, ...patch } }));
    log.state("selection · setBookingDraft", patch);
  }, []);

  const resetBookingDraft = useCallback(() => {
    setState((s) => ({ ...s, bookingDraft: EMPTY.bookingDraft }));
  }, []);

  const confirmAppointment: SelectionContextValue["confirmAppointment"] = useCallback((a) => {
    const appt: Appointment = { ...a, id: genId(), createdAt: Date.now() };
    setState((s) => ({
      ...s,
      appointments: [...s.appointments, appt],
      items: [],
      // Budget remis à zéro : chaque nouveau RDV repart sur un choix de budget
      // vierge (le serveur réinitialise déjà current_budget_bracket). Retour Djam L65 :
      // « si je reprends un RDV dans la foulée, le budget précédent était sélectionné d'office ».
      budget: null,
      bookingDraft: EMPTY.bookingDraft,
    }));
    // RDV confirmé → la sélection est consommée : on purge les brouillons d'étapes
    // pour que la prochaine envie reparte vierge.
    clearStepDrafts("look-");
    clearStepDrafts("piece-");
    clearStepDrafts("color-");
    log.state("selection · confirmAppointment", appt);
    return appt;
  }, []);

  const value = useMemo<SelectionContextValue>(
    () => ({
      ...state,
      hydrated,
      addItem,
      removeItem,
      replaceLookItems,
      clearSelection,
      setBudget,
      setBookingDraft,
      resetBookingDraft,
      confirmAppointment,
    }),
    [
      state,
      hydrated,
      addItem,
      replaceLookItems,
      removeItem,
      clearSelection,
      setBudget,
      setBookingDraft,
      resetBookingDraft,
      confirmAppointment,
    ],
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelection(): SelectionContextValue {
  const ctx = useContext(SelectionContext);
  if (!ctx) {
    throw new Error("useSelection doit être utilisé dans <SelectionProvider>");
  }
  return ctx;
}
